
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import tabsJson from './kpiSubtabs.json';
import BarChart from './barChart';
import PieChart from './Piechart';
import PolarAreaChart from './PolarAreaChart';
import LineChart from './LineChart';
import RadarChart from './RadarChart';
import DonutChart from './DonutChart';

type Subtab = { id: number; name: string; actual?: number; standard?: number; btnData?: { id: number; name: string; data?: number[]; labels?: string[]; chartType?: 'bar' | 'pie' | 'polar' }[]; data?: number[]; labels?: string[]; chartType?: 'bar' | 'pie' };
type MainTab = { id: number; name: string; subtabs: Subtab[] };


const KpiTabs: React.FC = () => {
  const mainTabs = tabsJson as MainTab[];
  const [value, setValue] = React.useState<number>(mainTabs[0].id);
  const [subValue, setSubValue] = React.useState<number | null>(mainTabs[0].subtabs?.[0]?.id ?? null);
  const [btnIdx, setBtnIdx] = React.useState<number>(0);

  const currentMain = mainTabs.find((m) => m.id === value) ?? mainTabs[0];
  const selectedSubtab = currentMain.subtabs.find((s) => s.id === subValue) ?? currentMain.subtabs?.[0];

  // Reset btnIdx to 0 when subtab changes
  React.useEffect(() => {
    setBtnIdx(0);
  }, [subValue]);

  const formatValue = (n?: number) => {
    if (n === undefined || n === null) return '-';
    // show percent if value is reasonable for a percent (<=100)
    if (n <= 100 && Math.abs(n - Math.round(n)) > 0) return `${n.toFixed(1)}%`;
    if (n <= 100 && Number.isInteger(n)) return `${n}%`;
    // otherwise show as integer or one decimal
    return Number(n % 1 === 0 ? n : Number(n.toFixed(1))).toString();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#232323' }}>
        KPI Dashboard Insights
      </Typography>

      <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
            px: 0,
            mt: 0.5,
          }}
        >
          {mainTabs.map((t) => {
            const selected = value === t.id;
            return (
              <Button
                key={t.id}
                onClick={() => {
                  setValue(t.id);
                  setSubValue(t.subtabs && t.subtabs.length ? t.subtabs[0].id : null);
                }}
                aria-pressed={selected}
                sx={{
                  textTransform: 'none',
                  border: 'none',
                  borderRadius: 999,
                  background: selected ? '#FFF7F2' : '#FFFFFF',
                  px: 2,
                  py: 0.6,
                  boxShadow: 'none',
                  color: selected ? '#FF6B35' : '#7A7A7A',
                  fontWeight: selected ? 700 : 600,
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  flex: '0 0 auto',
                }}
                variant="contained"
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t.name}
                </Typography>
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* KPI cards row (horizontal scroll) - render all subtabs of current main as metric cards */}
      <Box sx={{ mt: 2, width: '100%' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          {selectedSubtab?.name ?? currentMain.name}
        </Typography>

        <Box sx={{ overflowX: 'auto', py: 1, pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}
          >
            {currentMain.subtabs.map((metric, idx) => {
              const actual = metric.actual;
              const standard = metric.standard;
              const delta = actual !== undefined && standard !== undefined ? +(actual - standard) : undefined;
              const positive = (delta ?? 0) >= 0;
              const displayValue = formatValue(actual);
              const deltaLabel = delta === undefined ? '' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}% vs Standard`;
              return (
                <Box
                  key={metric.id}
                  sx={{
                    width: 180,
                    height: 145,
                    bgcolor: subValue === metric.id ? '#FFFFFF' : '#F8F9FA',
                    borderRadius: 2,
                    boxShadow: '0 4px 16px 0 rgba(16, 24, 40, 0.10)',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    position: 'relative',
                    gap: 0.5,
                    flex: '0 0 auto',
                    cursor: 'pointer',
                    border: subValue === metric.id ? '2px solid #FF6B35' : '2px solid transparent',
                  }}
                  onClick={() => setSubValue(metric.id)}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: idx === 0 ? '#FF7A59' : '#111827', mb: 0.5 }}>
                    {displayValue}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', mb: 1, minHeight: 36, fontWeight: 500 }}>
                    {metric.name}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {delta !== undefined && (
                    <Box
                      sx={{
                        width: 'fit-content',
                        px: 1.2,
                        py: 0.4,
                        borderRadius: 1,
                        bgcolor: positive ? '#ECFDF3' : '#FFF1F2',
                        color: positive ? '#027A48' : '#B42318',
                        fontWeight: 600,
                        fontSize: 13,
                        alignSelf: 'center',
                        mb: 0.5,
                      }}
                    >
                      {deltaLabel}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{selectedSubtab?.name ?? currentMain.name}</Typography>
          <Typography variant="body2" sx={{ bgcolor: '#FFF4F0', px: 1, py: 0.5, borderRadius: 2, color: '#E17E61' }}>
           Standard Rate : ≥ {selectedSubtab?.standard ?? 75}%
          </Typography>
        </Box>
       
        {/* Render btnData as a row of buttons if available for the selected subtab */}
        {selectedSubtab?.btnData && Array.isArray(selectedSubtab?.btnData) && selectedSubtab.btnData.filter(btn => btn.name && btn.name.trim() !== '').length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, overflowX: 'auto' }}>
            {selectedSubtab.btnData
              .filter(btn => btn.name && btn.name.trim() !== '')
              .map((btn, idx) => (
                <Button
                  key={btn.id}
                  variant="outlined"
                  onClick={() => setBtnIdx(idx)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    px: 2,
                    py: 0.5,
                    fontWeight: 600,
                    color: btnIdx === idx ? '#FF6B35' : '#7A7A7A',
                    borderColor: btnIdx === idx ? '#FF6B35' : '#7A7A7A',
                    background: btnIdx === idx ? '#FFF7F2' : '#FFFFFF',
                    '&:hover': {
                      background: '#FFF7F2',
                      borderColor: '#FF6B35',
                    },
                  }}
                >
                  {btn.name}
                </Button>
              ))}
          </Box>
        )}

        {/* Render chart based on chartType and btnData selection */}
        {(() => {
          const chartType = selectedSubtab?.btnData?.[btnIdx]?.chartType;
          let data = undefined;
          let labels = undefined;
          if (selectedSubtab?.btnData && selectedSubtab.btnData.length > 0 && selectedSubtab.btnData[btnIdx]) {
            data = selectedSubtab.btnData[btnIdx].data;
            labels = selectedSubtab.btnData[btnIdx].labels;
          } else {
            data = selectedSubtab?.data;
            labels = selectedSubtab?.labels;
          }
          if (chartType === 'bar') {
            return <BarChart data={data} labels={labels} />;
          } else if (chartType === 'pie') {
            return <PieChart data={data} labels={labels} />;
          } else if (chartType === 'polar') {
            return <PolarAreaChart data={data} labels={labels} />;
          } else if (chartType === 'line') {
            return <LineChart data={data} label={labels} />;
          } else if (chartType === 'radar') {
            return <RadarChart data={data} labels={labels} />;
          } else if (chartType === 'donut') {
            return <DonutChart values={data} label={labels}/>;
          }
          return null;
        })()}
      </Box>
    </Box>
  );
};

export default KpiTabs;
